import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { updateBrandContent } from '../../../../api/Settings';
import ContentField, { Setting } from './ContentField';
import { StoreDetailsStyle } from '../styles/StoreDetailsStyle';
import { useAlertModal } from '../../../../common/hooks';


interface BrandContentProps {
  brandId: number;
  fields: Setting[];
}

const BrandContent: React.FC<BrandContentProps> = ({ brandId, fields }) => {
  const { showAlert } = useAlertModal();
  const [isLoading, setIsLoading] = useState(false);
  const [brandContentFields, setBrandContentFields] = useState<Setting[]>(fields);

  useEffect(() => {
    setBrandContentFields(fields);
  }, [fields]);

  const handleValueChange = useCallback((id: number, newValue: string) => {
    setBrandContentFields((prev) =>
      prev.map((field) => (field.id === id ? { ...field, value: newValue } : field))
    );
  }, []);

  const handleUpdate = () => {
    setIsLoading(true);

    const payload = {
      brand_id: brandId,
      fields: brandContentFields.map((field) => ({
        id: field.id,
        key_name: field.key_name,
        value: field.value || '',
      })),
    };

    const handleResponse = (jsonRes: any, status: number) => {
      switch (status) {
        case 200:
        case 201:
          showAlert('success', jsonRes.message || 'Brand content updated successfully');
          break;
        case 409:
          showAlert('warning', jsonRes.error || 'Conflict occurred');
          break;
        default:
          if (jsonRes?.error) showAlert('error', jsonRes.error);
          else showAlert('error', 'Unknown error occurred');
          break;
      }
      setIsLoading(false);
    };

    updateBrandContent(payload, (jsonRes: any, status: number) => {
      handleResponse(jsonRes, status);
    });
  };

  const COLOR_OPTIONS = [
    { label: 'Slate', value: '#64748B' },
    { label: 'Stone', value: '#78716C' },
    { label: 'Emerald', value: '#10B981' },
    { label: 'Teal', value: '#14B8A6' },
    { label: 'Sky Blue', value: '#0EA5E9' },
    { label: 'Indigo', value: '#6366F1' },
    { label: 'Violet', value: '#8B5CF6' },
    { label: 'Rose', value: '#F43F5E' },
    { label: 'Amber', value: '#F59E0B' },
    { label: 'Lime', value: '#84CC16' },
  ];

  const OPTIONS_MAP: Record<string, { label: string; value: string }[]> = {
    primary_color: COLOR_OPTIONS,
    secondary_color: COLOR_OPTIONS,
  };

  const groups: Record<string, Setting[]> = {};

  for (const field of brandContentFields) {
    const group = field.group_name || 'general';
    if (!groups[group]) groups[group] = [];
    groups[group].push(field);
  }

  // sort fields inside each group
  Object.keys(groups).forEach((g) => {
    groups[g].sort((a, b) => a.sort_order - b.sort_order);
  });

  if (brandContentFields.length === 0) return null;

  return (
    <View>
      {Object.entries(groups).map(([groupName, groupFields]) => (
        <View key={groupName} style={{ marginBottom: 28 }}>

          {/* Section / group heading */}
          <Text
            style={{
              fontSize: 16,
              fontWeight: '700',
              color: '#111827',
              textTransform: 'capitalize',
              marginBottom: 14,
              paddingBottom: 6,
              borderBottomWidth: 1,
              borderBottomColor: '#e5e7eb',
            }}
          >
            {groupName.replace(/_/g, ' ')}
          </Text>

          <View style={[styles.inputGroup, { flexDirection: 'column', gap: 24 }]}>
            {groupFields.map((setting) => (
              <View key={setting.id}>
                <ContentField
                  setting={setting}
                  brandId={brandId}
                  onValueChange={handleValueChange}
                  customStyle={setting.field_type === 'color' ? { input: styles.select } : {}}
                  options={OPTIONS_MAP[setting.key_name]}
                />
              </View>
            ))}
          </View>
        </View>
      ))}

      <View style={{ alignItems: 'flex-end' }}>
        <TouchableOpacity onPress={handleUpdate}>
          <Text style={[styles.addButton, { marginBottom: 0, marginTop: 16, width: '100%' }]}>
            Save
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StoreDetailsStyle;

export default BrandContent;
